/**
 * Strategy · Foundations filter view (#936 D5).
 *
 * Filters Strategy Sections to `SectionType='Foundation'` for the active
 * company's Active StrategyDocument. Renders each foundation section with its
 * source-based ConfidenceLevel pill (Strong / Moderate / Weak), so the team
 * can scan evidence quality at a glance.
 *
 * Foundations are the durable beliefs (ICP / Positioning / Messaging /
 * UnitEconomics / GTMMotion). Plan sections are filtered out here.
 */
import { Component, Input, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { LogError, RunView } from '@memberjunction/core';
import { DeveloperModeService } from '@memberjunction/ng-shared';
import {
    marketingStrategyDocumentEntity,
    marketingStrategySectionEntity,
} from 'mj_generatedentities';
import { filterByType, type StrategySectionRow } from './strategy-section-helpers';

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-foundations',
    template: `
<mkt-ws-page-head
    eyebrow="Strategy · Foundations"
    title="Strategy foundations"
    [sub]="subText">
</mkt-ws-page-head>

<div *ngIf="loading" class="sf__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading foundations…</div>

<!--
    #938 / #937 — Empty-state CTA. Foundations get extracted by the
    onboarding diagnostic (website + blog corpus + LinkedIn signal).
    Route the user to the actual happy path instead of a passive
    "coming soon" tile.
-->
<div *ngIf="!loading && sections.length === 0" class="sf__empty">
    <i class="fa-solid fa-layer-group sf__empty-icon"></i>
    <h2 class="sf__empty-title">No Foundation sections yet.</h2>
    <p class="sf__empty-sub">
        Foundations cover ICP, Positioning, Messaging, Unit Economics, and
        GTM Motion. Start the strategy chat — a peer-CMO advisor will read
        your website + blog corpus and grill you on the weak spots until each
        foundation is sharp.
    </p>
    <div class="sf__empty-actions">
        <button type="button" class="sf__empty-btn sf__empty-btn--primary"
                (click)="goToChat()">
            <i class="fa-solid fa-comments"></i>
            Start strategy chat
            <i class="fa-solid fa-arrow-right"></i>
        </button>
    </div>
</div>

<div *ngIf="!loading && sections.length > 0" class="sf__summary">
    <div class="sf__summary-row">
        <div>
            <div class="sf__caps">Confidence summary</div>
            <div class="sf__pills">
                <span class="sf__pill sf__pill--good"><strong>{{ countByConfidence('Strong') }}</strong> Strong</span>
                <span class="sf__pill sf__pill--warn"><strong>{{ countByConfidence('Moderate') }}</strong> Moderate</span>
                <span class="sf__pill sf__pill--bad"><strong>{{ countByConfidence('Weak') }}</strong> Weak</span>
                <span class="sf__pill sf__pill--muted"><strong>{{ countByConfidence(null) }}</strong> Unset</span>
            </div>
        </div>
        <!-- Dev-mode only: archive everything to re-run onboarding. -->
        <button *ngIf="isDeveloperMode" type="button" class="sf__reset-btn"
                [disabled]="resetting"
                (click)="resetForTesting()"
                title="Archive all StrategyDocuments for this company so onboarding starts fresh">
            <i class="fa-solid" [class.fa-rotate-left]="!resetting" [class.fa-spinner]="resetting" [class.fa-spin]="resetting"></i>
            {{ resetting ? 'Archiving…' : 'Reset for testing' }}
        </button>
    </div>
</div>

<div *ngIf="!loading && sections.length > 0" class="sf__layout">
    <ng-container *ngFor="let s of sections">
        <mkt-ws-strategy-section-card
            [section]="s"
            [canEdit]="canEdit"
            (viewDiffsRequested)="onViewDiffs(s)">
        </mkt-ws-strategy-section-card>
        <!--
            #937 — "Discuss this gap →" affordance for Weak Foundations. Replaces the
            former "Interview now" link. Opens the same #951 fab-handoff stub used by
            the onboarding wizard's Step 5.
        -->
        <div *ngIf="s.ConfidenceLevel === 'Weak'" class="sf__gap-link">
            <button type="button" class="sf__gap-btn" (click)="discussGap(s)">
                <i class="fa-solid fa-comments"></i> Discuss this gap →
            </button>
        </div>
    </ng-container>
</div>

<mkt-ws-strategy-section-diff-modal
    [open]="diffModalOpen"
    [sectionId]="activeDiffSectionId"
    [sectionTitle]="activeDiffSectionTitle"
    [canEdit]="canEdit"
    (close)="diffModalOpen = false">
</mkt-ws-strategy-section-diff-modal>

<div *ngIf="discussDialogOpen" class="sf__overlay" (click)="discussDialogOpen = false">
    <div class="sf__dialog-card" (click)="$event.stopPropagation()">
        <header class="sf__dialog-head">Coming in #951</header>
        <p class="sf__dialog-body">
            The CMO Support Agent fab — the immersive interview surface for closing gaps —
            ships in card #951. Until then, this section's queued question lives in the
            agent's last-run output. Once the fab is wired up, this button will open it
            seeded with the question for <strong>{{ activeGapTitle }}</strong>.
        </p>
        <div class="sf__dialog-actions">
            <button class="sf__dialog-btn" type="button" (click)="discussDialogOpen = false">Got it</button>
        </div>
    </div>
</div>
    `,
    styles: [`
        :host { display: block; }
        .sf__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .sf__caps { font-size: 11px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin-bottom: 8px; }
        .sf__summary { max-width: 920px; margin-bottom: 20px; }
        .sf__summary-row { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; }
        .sf__reset-btn { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; font-size: 12px; font-weight: 500; border: 1px dashed var(--mj-status-warning, #d97706); border-radius: 6px; background: transparent; color: var(--mj-status-warning, #d97706); cursor: pointer; font-family: inherit; }
        .sf__reset-btn:hover:not(:disabled) { background: color-mix(in srgb, var(--mj-status-warning, #d97706) 8%, transparent); }
        .sf__reset-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .sf__pills { display: flex; gap: 8px; flex-wrap: wrap; }
        .sf__pill { padding: 4px 10px; border-radius: 999px; font-size: 12.5px; }
        .sf__pill--good { background: #dcfce7; color: #166534; }
        .sf__pill--warn { background: #fef9c3; color: #854d0e; }
        .sf__pill--bad { background: #fee2e2; color: #b91c1c; }
        .sf__pill--muted { background: #e5e7eb; color: #374151; }
        .sf__layout { display: flex; flex-direction: column; max-width: 920px; }
        .sf__gap-link { margin: -8px 0 16px; padding-left: 20px; }
        .sf__gap-btn { background: none; border: none; color: #b91c1c; font-size: 12.5px; cursor: pointer; padding: 4px 0; }
        .sf__gap-btn:hover { text-decoration: underline; }
        .sf__overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .sf__dialog-card { background: var(--mj-bg-surface, #fff); border-radius: 10px; padding: 20px 24px; max-width: 480px; width: 90%; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2); }
        .sf__dialog-head { font-size: 15px; font-weight: 600; margin-bottom: 12px; }
        .sf__dialog-body { font-size: 13.5px; line-height: 1.6; padding: 4px 0; margin: 0 0 16px; }
        .sf__dialog-actions { display: flex; justify-content: flex-end; }
        .sf__dialog-btn { padding: 6px 14px; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); cursor: pointer; font-size: 12.5px; }

        /* Empty-state CTA panel (#938 / #937 wiring). */
        .sf__empty { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 48px 24px; max-width: 560px; margin: 24px auto 0; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 12px; }
        .sf__empty-icon { font-size: 28px; color: var(--mj-text-secondary, #71717a); margin-bottom: 12px; }
        .sf__empty-title { font-size: 18px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin: 0 0 8px; }
        .sf__empty-sub { font-size: 13.5px; line-height: 1.55; color: var(--mj-text-secondary, #71717a); margin: 0 0 20px; }
        .sf__empty-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
        .sf__empty-btn { display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; font-size: 13px; font-weight: 500; font-family: inherit; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); cursor: pointer; }
        .sf__empty-btn--primary { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }
        .sf__empty-btn--primary:hover { opacity: 0.92; }
    `]
})
export class StrategyFoundationsSection implements OnDestroy {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.reset();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() companyName = '';
    @Input() canEdit = false;
    @Input() navigate: (path: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);
    private devModeService = inject(DeveloperModeService);
    private devModeSub?: Subscription;

    loading = false;
    document: marketingStrategyDocumentEntity | null = null;
    sections: StrategySectionRow[] = [];

    diffModalOpen = false;
    activeDiffSectionId: string | null = null;
    activeDiffSectionTitle = '';

    /** #937 — "Discuss this gap" stub for Weak foundations. */
    discussDialogOpen = false;
    activeGapTitle = '';

    isDeveloperMode = false;
    resetting = false;

    constructor() {
        this.devModeSub = this.devModeService.IsEnabled$.subscribe(enabled => {
            this.isDeveloperMode = enabled;
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.devModeSub?.unsubscribe();
    }

    discussGap(s: StrategySectionRow): void {
        this.activeGapTitle = s.Title || s.SectionKey;
        this.discussDialogOpen = true;
        this.cdr.detectChanges();
    }

    /** Empty-state CTA — routes to the conversational strategy onboarding agent. */
    goToChat(): void {
        this.navigate('onboarding/chat');
    }

    /**
     * Dev-mode reset. Sets every Active/Draft StrategyDocument for this company
     * to Superseded so the empty-state CTA reappears and onboarding starts fresh.
     * (Schema has no 'Archived' state — Superseded is the terminal state we have.)
     * Doesn't touch StrategySection rows; they hang off Superseded docs and get
     * filtered out by the load() query.
     */
    async resetForTesting(): Promise<void> {
        if (!this._companyId || this.resetting) return;
        const ok = window.confirm('Supersede all StrategyDocuments for this company? This returns Foundation to the empty state and lets the onboarding chat run fresh.');
        if (!ok) return;
        this.resetting = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const res = await rv.RunView<marketingStrategyDocumentEntity>({
                EntityName: 'Strategy Documents',
                ExtraFilter: `CompanyID='${this._companyId}' AND Status IN ('Active','Draft')`,
                ResultType: 'entity_object',
                MaxRows: 50,
            });
            if (!res.Success) {
                LogError(`[StrategyFoundationsSection] resetForTesting query failed: ${res.ErrorMessage}`);
                window.alert(`Reset query failed: ${res.ErrorMessage ?? 'unknown error'}`);
                return;
            }
            const docs = res.Results ?? [];
            for (const doc of docs) {
                doc.Status = 'Superseded';
                if (!await doc.Save()) {
                    LogError(`[StrategyFoundationsSection] failed to archive ${doc.ID}: ${doc.LatestResult?.Message ?? 'unknown'}`);
                }
            }
            await this.load();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyFoundationsSection] resetForTesting failed: ${msg}`);
            window.alert(`Reset failed: ${msg}`);
        } finally {
            this.resetting = false;
            this.cdr.detectChanges();
        }
    }

    get subText(): string {
        return `${this.sections.length} Foundation sections${this.document ? ' · ' + this.document.Status : ''}.`;
    }

    countByConfidence(level: 'Strong' | 'Moderate' | 'Weak' | null): number {
        return this.sections.filter(s => (s.ConfidenceLevel ?? null) === level).length;
    }

    onViewDiffs(s: StrategySectionRow): void {
        this.activeDiffSectionId = s.ID;
        this.activeDiffSectionTitle = s.Title || s.SectionKey;
        this.diffModalOpen = true;
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const docResult = await rv.RunView<marketingStrategyDocumentEntity>({
                EntityName: 'Strategy Documents',
                ExtraFilter: `CompanyID='${cid}'`,
                MaxRows: 5,
                ResultType: 'simple',
            });
            if (!docResult.Success) {
                LogError(`[StrategyFoundationsSection] doc query failed: ${docResult.ErrorMessage ?? 'unknown'}`);
                this.reset();
                return;
            }
            const rows = (docResult.Results ?? []) as marketingStrategyDocumentEntity[];
            const filtered = rows.filter(r => r.Status === 'Active' || r.Status === 'Draft');
            filtered.sort((a, b) => b.Version - a.Version);
            this.document = filtered.length > 0 ? filtered[0] : null;

            if (this.document) {
                const sectionResult = await rv.RunView<marketingStrategySectionEntity>({
                    EntityName: 'Strategy Sections',
                    ExtraFilter: `StrategyDocumentID='${this.document.ID}' AND SectionType='Foundation'`,
                    OrderBy: 'DisplayOrder ASC',
                    MaxRows: 100,
                    ResultType: 'simple',
                });
                if (sectionResult.Success) {
                    this.sections = filterByType((sectionResult.Results ?? []) as StrategySectionRow[], 'Foundation');
                } else {
                    LogError(`[StrategyFoundationsSection] sections query failed: ${sectionResult.ErrorMessage ?? 'unknown'}`);
                    this.sections = [];
                }
            } else {
                this.sections = [];
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyFoundationsSection] load failed: ${msg}`);
            this.reset();
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private reset(): void {
        this.document = null;
        this.sections = [];
        this.cdr.detectChanges();
    }
}
